import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useRef, useState } from 'react';
import cn from 'classnames';
import { getStroke } from 'perfect-freehand';
import { Card, Column, Row, SegmentControl, TextButton, TextField } from 'seagull';
import { TrashLine } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import styles from './SignatureInput.module.scss';
import { getSvgPathFromStroke } from './signatureInput.utils';
export const { SignatureInputTestIds } = getTestIds();
function capitalizeWords(str) {
    return str.split(/\s+/).map((word)=>word.length > 0 ? word[0].toUpperCase() + word.slice(1).toLowerCase() : '').join(' ');
}
export function SignatureInput(param) {
    let { value, onChange, disabled = false, placeholder = 'Type your name...', 'data-testid': testId } = param;
    const [mode, setMode] = useState((value === null || value === void 0 ? void 0 : value.type) === 'svg' ? 'draw' : 'type');
    const [typedValue, setTypedValue] = useState((value === null || value === void 0 ? void 0 : value.type) === 'text' ? value.text.value : '');
    const canvasRef = useRef(null);
    const isDrawingRef = useRef(false);
    const strokesRef = useRef([]);
    const currentStrokeRef = useRef([]);
    const hasValue = !!((value === null || value === void 0 ? void 0 : value.type) === 'text' && value.text.value.trim() || (value === null || value === void 0 ? void 0 : value.type) === 'svg' && value.svg.content);
    const handleTypeChange = useCallback((e)=>{
        const raw = e.target.value;
        const capitalized = capitalizeWords(raw);
        setTypedValue(capitalized);
        if (capitalized.trim()) {
            onChange({
                type: 'text',
                text: {
                    value: capitalized.trim()
                }
            });
        } else {
            onChange(null);
        }
    }, [
        onChange
    ]);
    const getPoint = useCallback((e)=>{
        var _e_touches_, _e_touches_1;
        const canvas = canvasRef.current;
        if (!canvas) return null;
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        const clientX = 'touches' in e ? (_e_touches_ = e.touches[0]) === null || _e_touches_ === void 0 ? void 0 : _e_touches_.clientX : e.clientX;
        const clientY = 'touches' in e ? (_e_touches_1 = e.touches[0]) === null || _e_touches_1 === void 0 ? void 0 : _e_touches_1.clientY : e.clientY;
        if (clientX == null || clientY == null) return null;
        const pressure = 'touches' in e ? 0.5 : 'pressure' in e ? e.pressure : 0.5;
        return [
            (clientX - rect.left) * scaleX,
            (clientY - rect.top) * scaleY,
            pressure
        ];
    }, []);
    const getSignatureColor = useCallback(()=>{
        const canvas = canvasRef.current;
        if (!canvas) return '#282863';
        const color = getComputedStyle(canvas).getPropertyValue('--signature-color').trim() || getComputedStyle(canvas).getPropertyValue('--text-on-standard-bg').trim() || getComputedStyle(canvas).getPropertyValue('--text-standard').trim();
        return color || '#282863';
    }, []);
    const redrawCanvas = useCallback((paths, currentPoints)=>{
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = getSignatureColor();
        paths.forEach((pathData)=>{
            const path = new Path2D(pathData);
            ctx.fill(path);
        });
        if (currentPoints && currentPoints.length >= 2) {
            const stroke = getStroke(currentPoints, {
                size: 4,
                thinning: 0.5
            });
            const pathData = getSvgPathFromStroke(stroke);
            if (pathData) {
                ctx.fill(new Path2D(pathData));
            }
        }
    }, [
        getSignatureColor
    ]);
    const handleDrawStart = useCallback((e)=>{
        e.preventDefault();
        if (disabled) return;
        const point = getPoint(e);
        if (point) {
            isDrawingRef.current = true;
            currentStrokeRef.current = [
                point
            ];
        }
    }, [
        disabled,
        getPoint
    ]);
    const handleDrawMove = useCallback((e)=>{
        e.preventDefault();
        if (!isDrawingRef.current || disabled) return;
        const point = getPoint(e);
        if (point) {
            currentStrokeRef.current.push(point);
            const paths = strokesRef.current.map((s)=>s.path);
            redrawCanvas(paths, currentStrokeRef.current);
        }
    }, [
        disabled,
        getPoint,
        redrawCanvas
    ]);
    const buildSvgFromStrokes = useCallback(()=>{
        const canvas = canvasRef.current;
        if (!canvas || strokesRef.current.length === 0) return null;
        const paths = strokesRef.current.map((s)=>s.path).filter(Boolean);
        if (paths.length === 0) return null;
        const fillColor = getSignatureColor();
        const pathElements = paths.map((p)=>'  <path d="'.concat(p, '" fill="').concat(fillColor, '"/>')).join('\n');
        return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 '.concat(canvas.width, " ").concat(canvas.height, '">\n').concat(pathElements, "\n</svg>");
    }, [
        getSignatureColor
    ]);
    const handleDrawEnd = useCallback(()=>{
        if (!isDrawingRef.current) return;
        isDrawingRef.current = false;
        const points = currentStrokeRef.current;
        currentStrokeRef.current = [];
        if (points.length >= 2) {
            const stroke = getStroke(points, {
                size: 4,
                thinning: 0.5
            });
            const pathData = getSvgPathFromStroke(stroke);
            if (pathData) {
                strokesRef.current.push({
                    path: pathData
                });
                const svg = buildSvgFromStrokes();
                if (svg) {
                    onChange({
                        type: 'svg',
                        svg: {
                            content: svg
                        }
                    });
                }
            }
        }
        redrawCanvas(strokesRef.current.map((s)=>s.path));
    }, [
        buildSvgFromStrokes,
        onChange,
        redrawCanvas
    ]);
    const handleClear = useCallback(()=>{
        setTypedValue('');
        strokesRef.current = [];
        currentStrokeRef.current = [];
        onChange(null);
        redrawCanvas([]);
    }, [
        onChange,
        redrawCanvas
    ]);
    const handleModeChange = useCallback((newMode)=>{
        if (disabled) return;
        setMode(newMode);
        if (newMode === 'type') {
            onChange(typedValue.trim() ? {
                type: 'text',
                text: {
                    value: typedValue.trim()
                }
            } : null);
        } else {
            const svg = buildSvgFromStrokes();
            onChange(svg ? {
                type: 'svg',
                svg: {
                    content: svg
                }
            } : null);
        }
    }, [
        disabled,
        typedValue,
        buildSvgFromStrokes,
        onChange
    ]);
    const effectiveTestId = testId !== null && testId !== void 0 ? testId : SignatureInputTestIds.Root();
    return /*#__PURE__*/ _jsx(Column, {
        className: cn(styles.root, disabled && styles.disabled),
        testId: effectiveTestId,
        children: /*#__PURE__*/ _jsxs(Card, {
            padding: "m",
            className: styles.signatureCard,
            children: [
                /*#__PURE__*/ _jsx(SegmentControl, {
                    options: [
                        {
                            value: 'type',
                            label: 'Type',
                            testId: SignatureInputTestIds.TypeTab()
                        },
                        {
                            value: 'draw',
                            label: 'Draw',
                            testId: SignatureInputTestIds.DrawTab()
                        }
                    ],
                    value: mode,
                    onChange: (v)=>handleModeChange(v),
                    variant: "text",
                    "aria-label": "Signature method"
                }),
                /*#__PURE__*/ _jsxs(Column, {
                    className: styles.inputArea,
                    children: [
                        /*#__PURE__*/ _jsx(Column, {
                            className: cn(styles.modePanel, styles.typeModePanel, {
                                [styles.hidden]: mode !== 'type'
                            }),
                            children: /*#__PURE__*/ _jsx(Column, {
                                className: cn(styles.modePanelContent, styles.typeModePanelContent),
                                children: /*#__PURE__*/ _jsx(TextField, {
                                    value: typedValue,
                                    onChange: handleTypeChange,
                                    placeholder: placeholder,
                                    disabled: disabled,
                                    fullWidth: true,
                                    isSignature: true,
                                    preventOutline: true,
                                    inputRootClass: styles.signatureInputRoot,
                                    textColor: "var(--text-on-standard-bg, var(--text-standard, #282863))",
                                    inputProps: {
                                        'data-testid': SignatureInputTestIds.TypeInput(),
                                        className: styles.signatureInputField
                                    }
                                })
                            })
                        }),
                        /*#__PURE__*/ _jsx(Column, {
                            className: cn(styles.modePanel, {
                                [styles.hidden]: mode !== 'draw'
                            }),
                            children: /*#__PURE__*/ _jsx(Column, {
                                className: styles.modePanelContent,
                                children: /*#__PURE__*/ _jsx("canvas", {
                                    ref: canvasRef,
                                    width: 400,
                                    height: 150,
                                    className: styles.canvas,
                                    onMouseDown: handleDrawStart,
                                    onMouseMove: handleDrawMove,
                                    onMouseUp: handleDrawEnd,
                                    onMouseLeave: handleDrawEnd,
                                    onTouchStart: handleDrawStart,
                                    onTouchMove: handleDrawMove,
                                    onTouchEnd: handleDrawEnd,
                                    onTouchCancel: handleDrawEnd,
                                    "data-testid": SignatureInputTestIds.DrawCanvas(),
                                    style: {
                                        touchAction: 'none'
                                    }
                                })
                            })
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(Row, {
                    className: styles.footer,
                    children: /*#__PURE__*/ _jsx(TextButton, {
                        className: styles.clearButton,
                        skin: "primary",
                        onClick: handleClear,
                        disabled: disabled || !hasValue,
                        testId: SignatureInputTestIds.ClearButton(),
                        "aria-label": "Clear signature",
                        prefixIcon: /*#__PURE__*/ _jsx(TrashLine, {}),
                        underline: "none",
                        weight: "normal",
                        children: "Clear"
                    })
                })
            ]
        })
    });
}
